package com.milta.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.rememberCoroutineScope
import com.milta.core.appconfig.AppConfigRepository
import com.milta.core.cache.LocalCacheStore
import com.milta.core.localization.LocalStrings
import com.milta.feature.delivery.domain.usecase.ValidateAddressUseCase
import com.milta.core.localization.WebImportRouteStrings
import com.milta.feature.importjobs.presentation.ImportJobTracker
import com.milta.feature.imports.presentation.documentErrorTexts
import com.milta.feature.route.domain.usecase.AddLoadingAddressUseCase
import com.milta.feature.route.domain.usecase.CreateRouteBatchUseCase
import com.milta.feature.route.domain.usecase.DeleteLoadingAddressUseCase
import com.milta.feature.route.domain.usecase.DispatchRouteBatchUseCase
import com.milta.feature.route.domain.usecase.GetLoadingAddressesUseCase
import com.milta.feature.route.domain.usecase.GetRouteDetailUseCase
import com.milta.feature.route.domain.usecase.UpdateDeliveryAddressUseCase
import com.milta.feature.route.domain.usecase.DeleteRouteBatchUseCase
import com.milta.core.localization.addToExistingHintText
import com.milta.core.localization.continueText
import com.milta.feature.route.domain.usecase.ReoptimizeRouteBatchUseCase
import com.milta.feature.optimizationquota.presentation.OptimizationQuotaStore
import com.milta.feature.optimizationquota.presentation.OptimizationQuotaFormat
import com.milta.feature.optimizationquota.presentation.fillPlaceholders
import com.milta.core.ui.component.ProcessingProgress
import com.milta.feature.aicredit.domain.entity.AiCredit
import com.milta.feature.aicredit.presentation.AiCreditStore
import org.jetbrains.compose.web.dom.B
import com.milta.feature.route.presentation.WebImportRouteIntent
import com.milta.feature.route.presentation.WebImportRouteNavEvent
import com.milta.feature.route.presentation.WebImportRoutePhase
import com.milta.feature.route.presentation.WebImportRouteState
import com.milta.feature.route.presentation.WebImportRouteViewModel
import com.milta.feature.team.domain.usecase.GetEnterpriseDriversUseCase
import com.milta.feature.team.domain.usecase.GetMyTeamUseCase
import kotlinx.coroutines.launch
import kotlinx.coroutines.suspendCancellableCoroutine
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.khronos.webgl.get
import org.koin.compose.koinInject
import org.w3c.files.File
import org.w3c.files.FileReader
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException

// Milta charte graphique — mêmes constantes que DashboardShell.kt/WebWelcomeScreen.kt
// (Compose HTML n'a pas accès à MiltaTheme, property() est la seule surface stable).
internal const val WebPrimary = "#2E5F7A"
internal const val WebPrimaryMid = "#4A90A4"
internal const val WebPrimaryLight = "#A8C8D8"
internal const val WebTeal = "#5CC8B8"
internal const val WebMint = "#8FDB8F"
internal const val WebTextPrimary = "#1E3A4A"
internal const val WebTextSecondary = "#6B8A9A"
internal const val WebSurface = "#FFFFFF"
internal const val WebSurfaceDim = "#E8EFF3"
internal const val WebBorder = "#D7DFE3"
internal const val WebError = "#D32F2F"
internal const val WebErrorBg = "#FBE3E2"
internal const val WebErrorInk = "#C62828"
internal const val WebServiceBg = "#F2E9DE"
internal const val WebServiceInk = "#8A5A2C"
internal const val WebWarning = "#C9820A"
internal const val WebWarningBg = "#FBF1DE"
internal const val WebSuccessBg = "#DDF3E4"
internal const val WebSuccessInk = "#2F5E42"
internal const val WebActiveTint = "#E3F5F2"

// RouteStatus badge (WebDashboardScreen) — kept in exact hex parity with the shared mobile
// tokens (Color.kt: AccentLight/Teal, InProgressBg/InProgressInk) so "Scheduled"/"In progress"
// read the same brand teal / teal-green across driver, manager-mobile and manager-web screens,
// instead of introducing a fourth, web-only color scheme for the same RouteStatus enum.
internal const val WebAccentLight = "#D6F2EE"
internal const val WebTealAccent = "#00B8A9"
internal const val WebInProgressBg = "#D8F3EE"
internal const val WebInProgressInk = "#157A68"

/** Reads a picked/dropped [File] fully into memory — same synchronous-upload shape as the mobile
 * pickers (`ByteArray` + file name) so [WebImportRouteViewModel] stays platform-agnostic. */
private suspend fun File.readAsByteArray(): ByteArray = suspendCancellableCoroutine { cont ->
    val reader = FileReader()
    reader.onload = {
        val buffer = reader.result as ArrayBuffer
        val array = Int8Array(buffer)
        cont.resume(ByteArray(array.length) { i -> array[i] })
    }
    reader.onerror = { cont.resumeWithException(IllegalStateException("file_read_error")) }
    reader.readAsArrayBuffer(this)
}

internal enum class WebImportRouteStep { IMPORT, VERIFY_ASSIGN, RESULT }

/**
 * Entry point wired from `Main.kt`'s "Ajouter une tournée" nav destination (MIL-50/MIL-51) — owns
 * the single [WebImportRouteViewModel] driving all three wizard steps and renders the shared
 * stepper (mockup's `.stepper`, always visible above either grid) before switching between
 * [WebImportRouteScreen] (Écran 1), [WebVerifyAssignRouteScreen] (Écran 2) and
 * [WebRouteBatchResultScreen] (Écran 3, MIL-51).
 */
@Composable
fun WebImportRouteContent(onDispatched: (confirmationMessage: String?) -> Unit) {
    val importJobTracker: ImportJobTracker = koinInject()
    val getMyTeam: GetMyTeamUseCase = koinInject()
    val getEnterpriseDrivers: GetEnterpriseDriversUseCase = koinInject()
    val createRouteBatch: CreateRouteBatchUseCase = koinInject()
    val dispatchRouteBatch: DispatchRouteBatchUseCase = koinInject()
    val getRouteDetail: GetRouteDetailUseCase = koinInject()
    val appConfigRepository: AppConfigRepository = koinInject()
    val localCacheStore: LocalCacheStore = koinInject()
    val validateAddress: ValidateAddressUseCase = koinInject()
    val getLoadingAddresses: GetLoadingAddressesUseCase = koinInject()
    val addLoadingAddress: AddLoadingAddressUseCase = koinInject()
    val deleteLoadingAddress: DeleteLoadingAddressUseCase = koinInject()
    val updateDeliveryAddress: UpdateDeliveryAddressUseCase = koinInject()
    val reoptimizeRouteBatch: ReoptimizeRouteBatchUseCase = koinInject()
    val optimizationQuotaStore: OptimizationQuotaStore = koinInject()
    val aiCreditStore: AiCreditStore = koinInject()
    val deleteRouteBatch: DeleteRouteBatchUseCase = koinInject()
    val viewModel = remember {
        WebImportRouteViewModel(
            importJobTracker,
            getMyTeam,
            getEnterpriseDrivers,
            createRouteBatch,
            dispatchRouteBatch,
            getRouteDetail,
            appConfigRepository,
            localCacheStore,
            validateAddress,
            getLoadingAddresses,
            addLoadingAddress,
            deleteLoadingAddress,
            updateDeliveryAddress,
            reoptimizeRouteBatch,
            optimizationQuotaStore,
            aiCreditStore,
            deleteRouteBatch,
        )
    }
    // MIL-98 — the Enterprise's AI credit (E1): exhausted banner, PDF refused at 0. Retour 29/10 —
    // the remaining credit itself is shown in Paramètres only.
    val aiCredit by aiCreditStore.credit.collectAsState()
    val state by viewModel.state.collectAsState()
    // MIL-97 — the Enterprise's own quota: "Optimiser l'itinéraire" and "Ré-optimiser" stop at 0.
    val quota by optimizationQuotaStore.quota.collectAsState()
    val strings = LocalStrings.current.webImportRoute

    // The ViewModel follows the app-wide import tracker: stopped when the page is left.
    DisposableEffect(viewModel) { onDispose { viewModel.clear() } }

    LaunchedEffect(viewModel) {
        viewModel.navEvent.collect { event ->
            when (event) {
                // state.batchResult/drivers are still set at the moment this event fires (the
                // ViewModel never clears them on a successful dispatch) — read here to compose the
                // confirmation banner shown once we land back on "Tournées du jour", since
                // WebImportRouteContent's own ViewModel is discarded on that navigation.
                WebImportRouteNavEvent.Dispatched -> onDispatched(buildDispatchConfirmationMessage(state, strings))
            }
        }
    }

    val step = when (state.phase) {
        WebImportRoutePhase.VerifyAssign -> WebImportRouteStep.VERIFY_ASSIGN
        WebImportRoutePhase.BatchResult -> WebImportRouteStep.RESULT
        else -> WebImportRouteStep.IMPORT
    }

    Div(attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("gap", "18px"); height(100.percent) } }) {
        val credit = aiCredit
        // Retour 26/10 (R6) — done steps are clickable, nothing lost: "1 Importer" adds a file to the
        // list, "2 Vérifier & assigner" returns to it (step 3 is reached through its button only).
        WebRouteStepper(
            current = step,
            strings = strings,
            reachedVerify = state.phase == WebImportRoutePhase.AddFile,
            onStepClick = { target ->
                when (target) {
                    WebImportRouteStep.IMPORT -> viewModel.onIntent(WebImportRouteIntent.ShowImportStep)
                    WebImportRouteStep.VERIFY_ASSIGN -> viewModel.onIntent(WebImportRouteIntent.ShowVerifyStep)
                    WebImportRouteStep.RESULT -> Unit
                }
            },
        )
        if (step == WebImportRouteStep.IMPORT && credit != null) {
            val ai = LocalStrings.current.aiCredit
            val date = OptimizationQuotaFormat.dayMonth(credit.resetAt)
            if (credit.isExhausted && state.phase == WebImportRoutePhase.Idle) {
                QuotaWarnBanner(
                    title = ai.exhaustedBannerWebTitle.fillPlaceholders("date" to date),
                    message = ai.exhaustedBannerWebMessage,
                )
            }
        }
        if (state.showCancelledNotice && state.phase == WebImportRoutePhase.Idle) {
            InlineBanner(kind = InlineBannerKind.Success, text = LocalStrings.current.imports.importCancelledNotice)
        }
        when (state.phase) {
            WebImportRoutePhase.VerifyAssign -> WebVerifyAssignRouteScreen(state = state, strings = strings, quota = quota, onIntent = viewModel::onIntent)
            WebImportRoutePhase.BatchResult -> WebRouteBatchResultScreen(state = state, strings = strings, quota = quota, onIntent = viewModel::onIntent)
            else -> WebImportRouteScreen(state = state, strings = strings, onIntent = viewModel::onIntent, aiCredit = aiCredit)
        }
    }
}

/** MIL-51 — a single driver's batch reuses the pre-existing "Tournée envoyée à {name}" wording
 * ([WebImportRouteStrings.dispatchSuccessMessage]); several drivers fall back to a count-based
 * message ([WebImportRouteStrings.dispatchSuccessBatchMessage]) since there's no single name to
 * show. Returns null if [WebImportRouteState.batchResult] is somehow absent (defensive only — the
 * dispatch intent itself is unreachable without one). */
private fun buildDispatchConfirmationMessage(state: WebImportRouteState, strings: WebImportRouteStrings): String? {
    val result = state.batchResult ?: return null
    return if (result.totalDrivers == 1) {
        val soleDriverId = result.drivers.firstOrNull()?.driverAccountId
        val driverName = state.drivers.firstOrNull { it.id == soleDriverId }?.let { "${it.firstName} ${it.lastName}".trim() }
        driverName?.let { strings.dispatchSuccessMessage.replace("%s", it) }
    } else {
        strings.dispatchSuccessBatchMessage.replace("%d", result.totalDrivers.toString())
    }
}

@Composable
internal fun WebRouteStepper(
    current: WebImportRouteStep,
    strings: WebImportRouteStrings,
    /** Retour 26/10 — a list exists: "2 Vérifier & assigner" is reachable from step 1 too. */
    reachedVerify: Boolean = false,
    onStepClick: (WebImportRouteStep) -> Unit = {},
    /** MIL-98 — right of the steps: the AI credit chip on step 1 (E1). */
    trailing: @Composable () -> Unit = {},
) {
    Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "10px") } }) {
        val importDone = current != WebImportRouteStep.IMPORT
        val verifyDone = current == WebImportRouteStep.RESULT || (current == WebImportRouteStep.IMPORT && reachedVerify)
        StepBubble(label = strings.stepImport, number = "1", done = importDone, active = current == WebImportRouteStep.IMPORT, onClick = { onStepClick(WebImportRouteStep.IMPORT) })
        StepLine(done = importDone)
        StepBubble(label = strings.stepVerifyAssign, number = "2", done = verifyDone, active = current == WebImportRouteStep.VERIFY_ASSIGN, onClick = { onStepClick(WebImportRouteStep.VERIFY_ASSIGN) })
        StepLine(done = current == WebImportRouteStep.RESULT)
        StepBubble(label = strings.stepValidateSend, number = "3", done = false, active = current == WebImportRouteStep.RESULT)
        Div(attrs = { style { property("margin-left", "auto") } }) { trailing() }
    }
}

@Composable
private fun StepBubble(label: String, number: String, done: Boolean, active: Boolean, onClick: () -> Unit = {}) {
    val color = when {
        done -> WebSuccessInk
        active -> WebPrimary
        else -> WebTextSecondary
    }
    Div(attrs = {
        // R6 — a done step is underlined and clickable.
        if (done) onClick { onClick() }
        style {
            property("display", "flex"); property("align-items", "center"); property("gap", "8px"); property("font-size", "13.5px"); property("color", color); property("font-weight", if (done || active) "700" else "400")
            if (done) {
                property("cursor", "pointer"); property("text-decoration", "underline"); property("text-underline-offset", "3px")
            }
        }
    }) {
        Div(
            attrs = {
                style {
                    width(22.px); height(22.px)
                    property("border-radius", "50%")
                    property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                    property("font-size", "11.5px"); property("font-weight", "700")
                    property("background", if (done || active) color else WebSurfaceDim)
                    property("color", if (done || active) "#FFFFFF" else WebTextSecondary)
                    property("border", "1px solid " + (if (done || active) color else WebBorder))
                    property("box-sizing", "border-box")
                }
            },
        ) { if (done) WebIcon(WebIconName.Check, size = 13, color = "#FFFFFF") else Text(number) }
        Text(label)
    }
}

@Composable
private fun StepLine(done: Boolean) {
    Div(attrs = { style { width(44.px); height(1.px); property("background", if (done) WebSuccessInk else WebBorder) } })
}

/** Écran 1/1b/1c/1d — dropzone (initial + analysing) or full-screen error banner, plus the
 * "Colonnes attendues" side-card, always shown side by side (mockup's `.import-grid`). */
@Composable
internal fun WebImportRouteScreen(
    state: WebImportRouteState,
    strings: WebImportRouteStrings,
    onIntent: (WebImportRouteIntent) -> Unit,
    aiCredit: AiCredit? = null,
) {
    val ai = LocalStrings.current.aiCredit
    val aiResetDate = OptimizationQuotaFormat.dayMonth(aiCredit?.resetAt)
    Div(attrs = { style { property("display", "grid"); property("grid-template-columns", "1fr 260px"); property("gap", "18px"); property("align-items", "start") } }) {
        when (val phase = state.phase) {
            WebImportRoutePhase.InvalidFile -> ErrorPanel(
                kind = BannerKind.Error,
                title = strings.invalidFileTitle,
                message = strings.invalidFileMessage.replace("%s", state.fileName),
            ) {
                FilePickerLabel(accept = ".xlsx,.xls,.csv,.pdf", onFilePicked = { bytes, name -> onIntent(WebImportRouteIntent.PickFile(bytes, name)) }) {
                    GradientButton(strings.chooseAnotherFileButton)
                }
            }

            WebImportRoutePhase.ExtractionFailed -> ErrorPanel(
                kind = BannerKind.Error,
                title = strings.extractionFailedTitle,
                message = strings.extractionFailedMessage.replace("%s", state.fileName),
            ) {
                FilePickerLabel(accept = ".xlsx,.xls,.csv,.pdf", onFilePicked = { bytes, name -> onIntent(WebImportRouteIntent.PickFile(bytes, name)) }) {
                    GradientButton(strings.chooseAnotherFileButton)
                }
            }

            is WebImportRoutePhase.TooManyStops -> ErrorPanel(
                kind = BannerKind.Error,
                title = strings.tooManyStopsError,
                message = "",
            ) {
                FilePickerLabel(accept = ".xlsx,.xls,.csv,.pdf", onFilePicked = { bytes, name -> onIntent(WebImportRouteIntent.PickFile(bytes, name)) }) {
                    GradientButton(strings.chooseAnotherFileButton)
                }
            }

            // "Réessayer" relaunches the import server-side; once both attempts are used it is
            // replaced by "Choisir un autre fichier" (maquette async-imports-v2, web écran 2).
            is WebImportRoutePhase.ServiceUnavailable -> ErrorPanel(
                kind = BannerKind.Service,
                title = strings.serviceUnavailableTitle,
                message = strings.serviceUnavailableMessage,
            ) {
                if (phase.offersRetry) {
                    Button(attrs = { onClick { onIntent(WebImportRouteIntent.RetryServiceCall) }; style { gradientButtonStyle(this) } }) { Text(strings.retryButton) }
                } else {
                    FilePickerLabel(accept = ".xlsx,.xls,.csv,.pdf", onFilePicked = { bytes, name -> onIntent(WebImportRouteIntent.PickFile(bytes, name)) }) {
                        GradientButton(strings.chooseAnotherFileButton)
                    }
                }
            }

            // The document itself, or its origin route deleted: same texts as the mobile import screens.
            is WebImportRoutePhase.JobFailed -> {
                val (title, subtitle, _) = LocalStrings.current.imports.documentErrorTexts(phase.kind)
                ErrorPanel(kind = BannerKind.Error, title = title, message = subtitle) {
                    FilePickerLabel(accept = ".xlsx,.xls,.csv,.pdf", onFilePicked = { bytes, name -> onIntent(WebImportRouteIntent.PickFile(bytes, name)) }) {
                        GradientButton(strings.chooseAnotherFileButton)
                    }
                }
            }

            // MIL-98 — E1 at 0: a PDF refused before any call, or a spreadsheet whose columns needed the AI.
            // Only "Choisir un autre fichier": no manual entry on the web dashboard.
            is WebImportRoutePhase.AiCreditExhausted -> ErrorPanel(
                kind = BannerKind.Error,
                title = if (phase.isSpreadsheet) ai.spreadsheetExhaustedTitle else ai.pdfRefusedTitle,
                message = (if (phase.isSpreadsheet) ai.spreadsheetExhaustedWebMessage else ai.pdfRefusedMessage)
                    .fillPlaceholders("file" to state.fileName, "date" to (OptimizationQuotaFormat.dayMonth(phase.resetAt).ifEmpty { aiResetDate })),
            ) {
                FilePickerLabel(accept = ".xlsx,.xls,.csv,.pdf", onFilePicked = { bytes, name -> onIntent(WebImportRouteIntent.PickFile(bytes, name)) }) {
                    GradientButton(strings.chooseAnotherFileButton)
                }
            }

            // Import of an existing route (or a photo scan) launched from the mobile app: its
            // deliveries belong to that route, it is finished on the phone.
            WebImportRoutePhase.ImportOnMobile -> ErrorPanel(
                kind = BannerKind.Service,
                title = LocalStrings.current.imports.activeImportHeader,
                message = strings.importOnMobileMessage.replace("%s", state.fileName),
            ) {}

            WebImportRoutePhase.NetworkUnavailable -> ErrorPanel(
                kind = BannerKind.Service,
                title = LocalStrings.current.imports.offlineTitle,
                message = LocalStrings.current.imports.offlineSubtitle,
            ) {
                Button(attrs = { onClick { onIntent(WebImportRouteIntent.RetryServiceCall) }; style { gradientButtonStyle(this) } }) { Text(strings.retryButton) }
            }

            WebImportRoutePhase.Analysing -> Dropzone(
                analysing = true,
                fileName = state.fileName,
                strings = strings,
                onIntent = onIntent,
                pdfUnavailableUntil = null,
                analysisStartedAtEpochMillis = state.analysisStartedAtEpochMillis,
                progress = state.analysisProgress,
            )

            // VerifyAssign/BatchResult never actually reach this composable (WebImportRouteContent
            // routes to WebVerifyAssignRouteScreen/WebRouteBatchResultScreen instead) — included only
            // for WebImportRoutePhase's exhaustive `when`.
            // Retour 26/10 (R9) — "1 Importer" with a list: a file dropped here is added to it.
            WebImportRoutePhase.AddFile -> Div {
                Dropzone(
                    analysing = false,
                    fileName = null,
                    strings = strings,
                    onIntent = onIntent,
                    pdfUnavailableUntil = aiResetDate.takeIf { aiCredit?.isExhausted == true },
                    existingCount = state.rows.size,
                )
            }

            WebImportRoutePhase.Idle, WebImportRoutePhase.VerifyAssign, WebImportRoutePhase.BatchResult ->
                Dropzone(
                    analysing = false,
                    fileName = null,
                    strings = strings,
                    onIntent = onIntent,
                    pdfUnavailableUntil = aiResetDate.takeIf { aiCredit?.isExhausted == true },
                )
        }

        ExpectedColumnsSideCard(strings, noCreditHint = ai.expectedColumnsNoCreditHint)
    }
}

@Composable
private fun Dropzone(
    analysing: Boolean,
    fileName: String?,
    strings: WebImportRouteStrings,
    onIntent: (WebImportRouteIntent) -> Unit,
    /** MIL-98 — AI credit at 0: the PDF format is left out of the constraints, with its return date. */
    pdfUnavailableUntil: String?,
    /** Retour 26/10 — start of the analysis (chrono) and the real step; null progress = server not reporting it yet. */
    analysisStartedAtEpochMillis: Long? = null,
    progress: ProcessingProgress? = null,
    /** Retour 26/10 (R9) — deliveries already in the list: the file is added to them, and the list is
     * one click away ("Continuer · N livraisons"). */
    existingCount: Int? = null,
    onBackToList: () -> Unit = { onIntent(WebImportRouteIntent.ShowVerifyStep) },
) {
    val scope = rememberCoroutineScope()

    Div(
        attrs = {
            style {
                property("background", WebSurface)
                property("border", "1.5px dashed $WebPrimaryLight")
                property("border-radius", "12px")
                property("min-height", "400px")
                property("display", "flex"); property("flex-direction", "column")
                property("align-items", "center"); property("justify-content", "center")
                property("text-align", "center"); property("gap", "6px")
                padding(32.px)
                property("box-sizing", "border-box")
            }
            if (!analysing) {
                onDragOver { it.preventDefault() }
                onDrop { event ->
                    event.preventDefault()
                    val file = event.dataTransfer?.files?.item(0)
                    if (file != null) {
                        scope.launch {
                            runCatching { file.readAsByteArray() }
                                .onSuccess { bytes -> onIntent(WebImportRouteIntent.PickFile(bytes, file.name)) }
                        }
                    }
                }
            }
        },
    ) {
        Div(
            attrs = {
                style {
                    width(64.px); height(64.px)
                    property("border-radius", "50%")
                    property("background", WebActiveTint)
                    property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                    property("margin-bottom", "10px")
                }
            },
        ) {
            // Couvre tout le temps d'envoi + analyse (un seul intent PickFile déclenche les deux côté
            // ViewModel, voir WebImportRouteViewModel.importFile — phase Analysing dès la sélection).
            if (analysing) {
                WebSpinner()
            } else {
                WebIcon(WebIconName.Upload, size = 28, color = WebPrimary)
            }
        }

        if (analysing && progress != null) {
            // Maquette W1: active step + chrono, file name, the 4 real steps, then "you can leave".
            val common = LocalStrings.current.common
            val now = rememberTickingNow()
            WebImportProgressTitle(
                title = "${progress.activeLabel(common)}…",
                chrono = analysisStartedAtEpochMillis?.let { formatChrono((now - it) / 1_000) },
            )
            if (fileName != null) {
                Div(attrs = { style { property("color", WebTextSecondary); property("font-size", "13px"); property("margin-top", "4px") } }) {
                    Text(fileName)
                }
            }
            WebImportProgressSteps(progress, common, now)
            Div(attrs = { style { property("color", WebInProgressInk); property("font-size", "13.5px"); property("font-weight", "700"); property("margin-top", "8px") } }) {
                Text(strings.analysingCanLeaveLabel)
            }
        } else if (analysing) {
            Div(attrs = { style { property("color", WebTextPrimary); property("font-size", "19px"); property("font-weight", "700") } }) {
                Text(strings.analysingLabel)
            }
            // The import runs server-side: leaving the page does not stop it (maquette
            // async-imports-v2, web écran 1 — same line, in-progress color).
            Div(attrs = { style { property("color", WebInProgressInk); property("font-size", "13.5px"); property("font-weight", "700"); property("margin-top", "8px") } }) {
                Text(strings.analysingCanLeaveLabel)
            }
            if (fileName != null) {
                Div(attrs = { style { property("color", WebTextSecondary); property("font-size", "13px"); property("margin-top", "6px") } }) {
                    Text(fileName)
                }
            }
        } else {
            Div(attrs = { style { property("color", WebTextPrimary); property("font-size", "19px"); property("margin", "0") } }) {
                Text(strings.dropzoneTitle)
            }
            Div(attrs = { style { property("font-size", "13px"); property("color", WebTextSecondary); property("margin", "2px 0 14px") } }) {
                Text("${strings.dropzoneOrPrefix} ")
                FilePickerLabel(accept = ".xlsx,.xls,.csv,.pdf", onFilePicked = { bytes, name -> onIntent(WebImportRouteIntent.PickFile(bytes, name)) }) {
                    Span(attrs = { style { property("color", WebPrimaryMid); property("font-weight", "600"); property("text-decoration", "none") } }) {
                        Text(strings.browseFilesLink)
                    }
                }
            }
            FilePickerLabel(accept = ".xlsx,.xls,.csv,.pdf", onFilePicked = { bytes, name -> onIntent(WebImportRouteIntent.PickFile(bytes, name)) }) {
                GradientButtonLabel(strings.browseFilesButton)
            }
            Div(attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary); property("margin-top", "14px") } }) {
                if (pdfUnavailableUntil == null) {
                    Text(strings.constraintsLabel)
                } else {
                    val ai = LocalStrings.current.aiCredit
                    Text("${ai.dropConstraintsWithoutPdf} · ")
                    B { Text(ai.pdfUnavailableUntil.fillPlaceholders("date" to pdfUnavailableUntil)) }
                }
            }
            if (existingCount != null) {
                val routeCreation = LocalStrings.current.routeCreation
                Div(attrs = { style { property("font-size", "12.5px"); property("color", WebTextSecondary); property("margin-top", "10px") } }) {
                    Text(routeCreation.addToExistingHintText(existingCount))
                }
                Button(
                    attrs = {
                        onClick { onBackToList() }
                        style { secondaryButtonStyle(this); property("margin-top", "10px") }
                    },
                ) { Text(routeCreation.continueText(existingCount)) }
            }
        }
    }
}

@Composable
private fun ExpectedColumnsSideCard(strings: WebImportRouteStrings, noCreditHint: String) {
    Div(
        attrs = {
            style {
                property("background", WebSurface)
                property("border", "1px solid $WebBorder")
                property("border-radius", "12px")
                padding(18.px)
            }
        },
    ) {
        Div(
            attrs = {
                style {
                    property("font-size", "10.5px"); property("font-weight", "700")
                    property("letter-spacing", "0.06em"); property("text-transform", "uppercase")
                    property("color", WebTextSecondary); property("margin-bottom", "12px")
                }
            },
        ) { Text(strings.expectedColumnsEyebrow) }

        ColumnRow(strings.columnRecipientName, required = true, pillLabel = strings.requiredPillLabel)
        ColumnRow(strings.columnAddress, required = true, pillLabel = strings.requiredPillLabel)
        ColumnRow(strings.columnDeadline, required = false, pillLabel = strings.optionalPillLabel)
        ColumnRow(strings.columnPhone, required = false, pillLabel = strings.optionalPillLabel)
        // MIL-98 — R7: a file with these columns is imported without the AI, even at 0 %.
        Div(attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary); property("margin-top", "10px"); property("line-height", "1.45") } }) {
            Text(noCreditHint)
        }
    }
}

@Composable
private fun ColumnRow(label: String, required: Boolean, pillLabel: String) {
    Div(
        attrs = { style { property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between"); property("padding", "7px 0"); property("font-size", "13.5px") } },
    ) {
        Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("color", WebTextPrimary) } }) {
            Span(attrs = { style { property("color", if (required) WebSuccessInk else WebTextSecondary); property("margin-right", "8px"); property("font-weight", "700") } }) {
                Text(if (required) "✓" else "·")
            }
            Text(label)
        }
        Div(
            attrs = {
                style {
                    property("font-size", "10px"); property("font-weight", "700")
                    property("padding", "2px 8px"); property("border-radius", "999px")
                    property("background", if (required) WebSuccessBg else WebSurfaceDim)
                    property("color", if (required) WebSuccessInk else WebTextSecondary)
                }
            },
        ) { Text(pillLabel) }
    }
}

internal enum class BannerKind { Error, Service }

@Composable
internal fun ErrorPanel(kind: BannerKind, title: String, message: String, actions: @Composable () -> Unit) {
    val (bg, ink) = when (kind) {
        BannerKind.Error -> WebErrorBg to WebErrorInk
        BannerKind.Service -> WebServiceBg to WebServiceInk
    }
    Div(
        attrs = {
            style {
                property("background", WebSurface); property("border", "1px solid $WebBorder")
                property("border-radius", "12px"); property("min-height", "380px")
                property("display", "flex"); property("flex-direction", "column")
                property("align-items", "center"); property("justify-content", "center"); property("text-align", "center")
                padding(16.px, 18.px)
            }
        },
    ) {
        Div(
            attrs = {
                style {
                    width(56.px); height(56.px); property("border-radius", "50%")
                    property("background", bg)
                    property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                    property("margin-bottom", "14px")
                }
            },
        ) { Div(attrs = { style { property("color", ink); property("font-size", "22px"); property("font-weight", "700") } }) { Text("!") } }
        Div(attrs = { style { property("font-size", "18px"); property("margin", "0 0 6px"); property("color", ink); property("font-weight", "700") } }) { Text(title) }
        Div(attrs = { style { property("font-size", "13.5px"); property("margin", "0 0 20px"); property("max-width", "360px"); property("line-height", "1.5"); property("color", WebTextSecondary) } }) { Text(message) }
        Div(attrs = { style { property("display", "flex"); property("gap", "10px") } }) { actions() }
    }
}

@Composable
internal fun GradientButton(text: String) {
    Div(attrs = { style { gradientButtonStyle(this) } }) { Text(text) }
}

@Composable
private fun GradientButtonLabel(text: String) {
    Div(attrs = { style { gradientButtonStyle(this) } }) { Text(text) }
}

internal fun gradientButtonStyle(scope: org.jetbrains.compose.web.css.StyleScope) = with(scope) {
    property("display", "inline-flex"); property("align-items", "center"); property("justify-content", "center")
    property("gap", "8px")
    property("background", "linear-gradient(120deg, $WebTeal, $WebMint)")
    property("color", "#FFFFFF"); property("font-size", "14px"); property("font-weight", "700")
    property("border", "none"); property("border-radius", "10px")
    property("padding", "12px 22px"); property("cursor", "pointer")
    property("box-shadow", "0 6px 16px rgba(92,200,184,0.35)")
}

/** Retour client — "Remplacer le fichier" et "Annuler l'import" (Écran 2) rendaient chacun un style
 * différent (lien texte simple vs autre traitement), à côté du vrai bouton CTA "Optimiser
 * l'itinéraire" ([gradientButtonStyle]) : résultat peu lisible, pas évident que ce sont deux actions
 * de même poids. Un seul style secondaire partagé (bordure + fond plein, pas un simple lien coloré)
 * pour toute action secondaire d'Écran 2 — seule la couleur distingue une action destructive
 * ([danger]) d'une action neutre, la forme reste identique. */
internal fun secondaryButtonStyle(scope: org.jetbrains.compose.web.css.StyleScope, danger: Boolean = false) = with(scope) {
    property("display", "inline-flex"); property("align-items", "center"); property("justify-content", "center")
    property("gap", "6px")
    // Fond teinté (pas blanc sur blanc) — même convention couleur que le reste de webApp
    // (WebErrorBg/WebErrorInk pour une action destructive, WebActiveTint/WebPrimary sinon).
    property("background", if (danger) WebErrorBg else WebActiveTint)
    property("color", if (danger) WebErrorInk else WebPrimary)
    property("font-size", "12.5px"); property("font-weight", "600")
    property("border", "1.5px solid " + (if (danger) WebError else WebPrimaryMid))
    property("border-radius", "8px")
    property("padding", "7px 14px"); property("cursor", "pointer")
}

/** Wraps a hidden `<input type="file">` in a `<label>` so [content] (any composable — icon, text,
 * styled button) triggers the native file picker when clicked, without needing a DOM [ref] to call
 * `.click()` manually. Mirrors the standard HTML "styled file input" technique. */
@Composable
internal fun FilePickerLabel(
    accept: String,
    onFilePicked: (bytes: ByteArray, fileName: String) -> Unit,
    content: @Composable () -> Unit,
) {
    val scope = rememberCoroutineScope()
    Label(attrs = { style { property("cursor", "pointer") } }) {
        content()
        Input(
            type = InputType.File,
            attrs = {
                attr("accept", accept)
                style { property("display", "none") }
                onChange { event ->
                    val file = event.target.files?.item(0)
                    if (file != null) {
                        scope.launch {
                            runCatching { file.readAsByteArray() }
                                .onSuccess { bytes -> onFilePicked(bytes, file.name) }
                        }
                    }
                }
            },
        )
    }
}
